<template>
  <main-layout
    controlsBg="light"
    customControls
    :breadcrumb="getBreadcrumb"
    :showPoweredBy="false"
    :has-settings="true">
    <explorer-topbar-settings
      slot="settings"
      v-on:onClickDownload="onClickDownload"
      v-on:onClickCopy="showCopyModal"
      v-on:onClickSave="showSaveModal"/>
    <div slot="custom-controls-top">
      <div class="controls__filters note">
        <controls-filter
          dateId="startDate"
          :filterTitle="$t('explorer.from')"
          formatDate="DD MMMM YYYY / HH:mm:ss"
          :disabledDate="disabledDate"
          :startDate="getStartDateString">
        </controls-filter>
        <controls-filter
          dateId="endDate"
          :filterTitle="$t('explorer.to')"
          formatDate="DD MMMM YYYY / HH:mm:ss"
          :disabledDate="disabledDate"
          :startDate="getEndDateString">
        </controls-filter>
      </div>
      <btn-pill
        class="update-button"
        :btnText="$t('explorer.menu.explore')"
        btn-size="xs"
        v-on:click="requestData">
      </btn-pill>
    </div>
    <div class="container">
      <div class="header">
        <div class="header__title">
          <h1
            class="flex">
            <chevron
              @click="back()"/>
            {{$t('menu.correlation')}}
            <div
              class="flex"
              v-if="getSampledVariables().length > 0"
              v-tooltip.right.top-start="{ content: '<b>' + $t('explorer.sampledVariables') + '</b></br>' + getSampledVariablesTooltipContent }">
              <font-awesome-icon
                class="icon"
                :icon="icon.exclamation"/>
            </div>
          </h1>
        </div>
        <options-correlation
          ref="optionsCorrelation"
          :hasIntensity="zType === 'intensity'"
          :enabledRange=true
          v-on:onFixedAxes="onFixedAxes"
          v-on:onEvolution="onEvolution"
          v-on:onGridStateChange="onGridStateChange"
          v-on:onSwitchMeter="onSwitchMeter"
          v-on:onExchangeAxes="onExchangeAxes"/>
      </div>
      <div
        id="scatters-container-parent"
        class="scatters-container-parent">
        <div
          ref="scattersContainer"
          id="scatters-container"
          class="scatters-container">
          <chart-correlation
            v-for="(chart, index) in charts"
            :key="index"
            :chartId="index"
            :data="getCorrelationData"
            :meter="meter"
            :range="range"
            :zRange="zRange"
            :grid="grid"
            :x="Number(chart.x)"
            :y="Number(chart.y)"
            :z="zVariables !== undefined ? zVariables[index] : null"
            :zCategory="zCategories[index]"
            :height="chartSize.height"
            :width="chartSize.width" />
        </div>
        <chart-meter
          id="chart-meter"
          class="chart-meter-container"
          :type="meter"
          :range="range"
          :zRange="meterRange"
          :data="getCorrelationData"
          :z="zVariables[0]"
          v-if="zVariables !== undefined && zType === 'intensity'" />
      </div>
    </div>
    <copy-url-modal
      ref="copyUrlModal"/>
    <query-save-modal
      ref="querySaveModal"
      v-on:save="createExplorerQuery"/>
    <fixed-range-modal
      ref="fixedRangeModal"
      :data="getCorrelationData"
      :selected-range="range"
      :x="charts[0] !== undefined ? Number(charts[0].x) : null"
      :y="charts[0] !== undefined ? Number(charts[0].y) : null"
      :z="zVariables !== undefined ? zVariables[0] : null"
      :hasIntensity="zType === 'intensity'"
      v-on:change="onFixedAxesRangeChanged"/>
    <variable-pair-select-modal
      :variablePairs="variablePairs"
      :variables="getCorrelationData.variables"
      v-on:onPairSelected="onPairSelected"
      ref="variablePairSelectModal">
    </variable-pair-select-modal>
  </main-layout>
</template>

<script>
import {eventBus} from '@/main';
import config from 'BRAND/config';
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import ControlsFilter from '@/components/shared/controlsbar/ControlsFilter';
import dayjs from 'dayjs';
import BtnPill from '@/components/shared/buttons/BtnPill';
import AcceptModal from '@/components/shared/dialogs/AcceptModal';
import Checkbox from '@/components/shared/forms/Checkbox';
import InlineTip from '@/components/explorer/InlineTip';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faExclamationCircle} from '@fortawesome/pro-regular-svg-icons';
import ChartCorrelation from '@/components/explorer/correlation/ChartCorrelation';
import ChartMeter from '@/components/explorer/correlation/ChartMeter';
import OptionsCorrelation from '@/components/explorer/correlation/OptionsCorrelation';
import CopyUrlModal from '@/components/explorer/CopyUrlModal';
import QuerySaveModal from '../../components/explorer/QuerySaveModal';
import FixedRangeModal from '@/components/explorer/correlation/FixedRangeModal';
import ExplorerTopbarSettings from '@/components/explorer/ExplorerTopbarSettings';
import explorerToCSV from '@/layouts/explorer/explorerToCSV';
import VariablePairSelectModal from '@/components/explorer/correlation/VariablePairSelectModal';
import Chevron from '@/components/shared/Chevron';
import navigation from '@/layouts/explorer/navigation';

export default {
  name: 'ExplorerCorrelation',
  components: {
    VariablePairSelectModal,
    MainLayout,
    ControlsFilter,
    BtnPill,
    AcceptModal,
    Checkbox,
    InlineTip,
    FontAwesomeIcon,
    ChartMeter,
    ChartCorrelation,
    CopyUrlModal,
    QuerySaveModal,
    OptionsCorrelation,
    FixedRangeModal,
    ExplorerTopbarSettings,
    Chevron
  },
  data () {
    return {
      startDate: getInitialStartDate(),
      endDate: getInitialEndDate(),
      zVariables: undefined,
      variablePairs: [],
      icon: {
        exclamation: faExclamationCircle
      },
      isMounted: false,
      range: { x: [], y: [] },
      zRange: [],
      meterRange: [],
      grid: false,
      meter: 'divergent',
      zType: '',
      zCategories: [],
      origin: null,
      filter: null
    };
  },
  created () {
    const query = this.$route.query;
    if (query.origin) {
      this.origin = query.origin;
    }
    if (query.filter) {
      this.filter = query.filter;
    }

    // Dates
    if (query.startDate && query.endDate) {
      this.startDate = new Date(Number(query.startDate));
      this.endDate = new Date(Number(query.endDate));
    }

    // Variable pairs
    for (let i = 0; i < 4; i++) {
      const pair = query['pair' + (i + 1)];
      if (pair) {
        this.variablePairs.push({
          x: pair.split(',')[0],
          y: pair.split(',')[1]
        });
      }
    }

    // Variable intensity
    if (query.z) {
      if (query.zType === 'category') {
        this.zVariables = query.z.split(',').map(zId => zId !== '_' ? Number(zId) : null);
      } else {
        this.zVariables = [];
        for (let i = 0; i < this.variablePairs.length; i++) {
          this.zVariables.push(Number(query.z));
        }
      }
    }

    // Options
    if (query.optRangeX && query.optRangeY) {
      this.range.x = query.optRangeX;
      this.range.y = query.optRangeY;
    }
    if (query.optRangeZ) {
      this.zRange = query.optRangeZ;
    }
    if (query.optGrid && query.optGrid === 'true') {
      this.grid = true;
    }
    if (query.optMeter) {
      this.meter = query.optMeter;
    }

    if (query.zType) {
      this.zType = query.zType;
    }

    // Category or Intensity & Request data
    if (this.zType === 'category') {
      this.retrieveCategories({ context: this }).then(() => {
        this.zVariables.forEach(zCategory => {
          if (zCategory !== null) {
            this.zCategories.push(this.getCategories.find(category => category.variables.includes(zCategory)));
          } else {
            this.zCategories.push(null);
          }
        });
        this.requestData();
      });
    } else {
      this.requestData();
    }

    eventBus.$on('dateSelected', this.onDateSelected);
  },
  mounted () {
    this.isMounted = true;
    this.$refs.fixedRangeModal.setRange(this.range, this.zRange);
    this.$refs.optionsCorrelation.setGridState(this.grid);
    this.$refs.optionsCorrelation.setMeter(this.meter);
  },
  destroyed () {
    eventBus.$off('dateSelected', this.onDateSelected);
  },
  computed: {
    ...mapGetters([
      'getLocale',
      'getCorrelationData',
      'getCategories'
    ]),
    charts () {
      return this.isMounted ? this.variablePairs : [];
    },
    chartSize () {
      return getScatterSize(this.variablePairs.length, this.zType);
    },
    getStartDateString () {
      return this.startDate.toString();
    },
    getEndDateString () {
      return this.endDate.toString();
    },
    getBreadcrumb () {
      if (this.filter) {
        return navigation.filteredBreadcrumb(this);
      } else {
        switch (this.origin) {
          case 'QueryList':
            return [
              {link: '/workspace', nameKey: 'menu.workspace'},
              {link: '/query/list', nameKey: 'query.title'},
              {nameKey: 'menu.correlation', disabled: true}
            ].map(route => {
              route.name = this.$t(route.nameKey);
              return route;
            });
          default:
            let relativePath = this.$route.fullPath.split('?')[1];
            if (relativePath.includes('&optRange')) {
              relativePath = relativePath.substring(0, relativePath.indexOf('&optRange'));
            }
            return [
              {name: this.$t('explorer.breadcrumbParentOldExplorer'), link: '/explore'},
              {name: this.$t('explorer.parameterSelection'), link: '/explorer-correlation-variables?' + relativePath}, // Dirty hack to emulate 'back' button
              {name: this.$t('menu.correlation'), disabled: true}
            ];
        }
      }
    },
    getSampledVariablesTooltipContent () {
      let variables = [];
      for (let i = 0; i < this.getSampledVariables().length; i++) {
        variables.push(this.getSampledVariables()[i].metadata.label);
      }
      return variables.join(',</br>');
    }
  },
  methods: {
    ...mapActions([
      'retrieveCorrelationData',
      'retrieveCategories',
      'createQuery'
    ]),
    back () {
      switch (this.origin) {
        case 'QueryList':
          const backQuery = this.filter ? {filter: navigation.FILTER_EXPLORER_CORRELATION} : null;
          this.$router.push({name: 'QueryList', query: backQuery});
          break;
        default:
          let query = this.$route.query;
          delete query.optRangeX;
          delete query.optRangeY;
          delete query.optRangeZ;
          this.$router.push({name: 'ExplorerCorrelationVariables', query: query});
      }
    },
    disabledDate (date) {
      return date > new Date();
    },
    onDateSelected (event) {
      if (event.id === 'startDate') {
        this.startDate = event.date;
      }
      if (event.id === 'endDate') {
        this.endDate = event.date;
      }

      const query = this.$route.query;
      const routeQuery = {
        query: {
          startDate: this.startDate.getTime(),
          endDate: this.endDate.getTime()
        }
      };

      // Variable pairs
      for (let i = 0; i < 4; i++) {
        const pair = query['pair' + (i + 1)];
        if (pair) {
          routeQuery.query['pair' + (i + 1)] = pair;
        }
      }

      // Variable intensity
      if (query.z) {
        routeQuery.query['z'] = query.z;
      }

      // Options
      if (query.optRangeX && query.optRangeY) {
        routeQuery.query['optRangeX'] = query.optRangeX;
        routeQuery.query['optRangeY'] = query.optRangeY;
      }
      if (query.optRangeZ) {
        routeQuery.query['optRangeZ'] = query.optRangeZ;
      }
      if (query.optGrid && query.optGrid === 'true') {
        this.grid = true;
      }
      if (query.optMeter) {
        this.meter = query.optMeter;
      }
      if (query.zType) {
        routeQuery.query['zType'] = query.zType;
      }
      if (this.origin) {
        routeQuery.query['origin'] = this.origin;
      }
      if (this.filter) {
        routeQuery.query['filter'] = this.filter;
      }

      this.$router.push(routeQuery);
    },
    requestData () {
      const payload = {
        context: this,
        variableIds: this.listVariableIds().join(','),
        startDate: this.startDate,
        endDate: this.endDate
      };
      this.retrieveCorrelationData(payload);
    },
    createExplorerQuery (options) {
      const queryName = options.name;
      const queryWithDates = options.dates;

      let relativePath = this.$route.fullPath;
      relativePath = relativePath.replace('&filter=' + this.filter, '');
      relativePath = relativePath.replace('&origin=' + this.origin, '');
      if (!queryWithDates) {
        // Remove dates from path
        const queryPath = this.$route.query;
        relativePath = relativePath.replace('&startDate=' + queryPath.startDate, '');
        relativePath = relativePath.replace('?startDate=' + queryPath.startDate, '?');
        relativePath = relativePath.replace('&endDate=' + queryPath.endDate, '');
        relativePath = relativePath.replace('?endDate=' + queryPath.endDate, '?');
        relativePath = relativePath.replace('?&', '?');
      }

      const query = {
        name: queryName,
        relativePath: relativePath,
        star: false
      };
      this.createQuery({ context: this, query: query });
    },
    showCopyModal () {
      this.$refs.copyUrlModal.show();
    },
    showSaveModal () {
      this.$refs.querySaveModal.show();
    },
    onFixedAxes () {
      this.$refs.fixedRangeModal.show();
    },
    onEvolution () {
      const query = {
        variables: this.listVariableIds().join((',')),
        startDate: this.startDate.getTime(),
        endDate: this.endDate.getTime()
      };
      this.$router.push({name: 'ExplorerEvolution', query: query});
    },
    onGridStateChange (gridStatus) {
      this.grid = gridStatus;
      this.updateUrl();
    },
    onSwitchMeter (meter) {
      this.meter = meter;
      this.updateUrl();
    },
    onFixedAxesRangeChanged (range) {
      this.range = range;
      this.zRange = range.z;
      this.updateUrl();
    },
    onExchangeAxes () {
      for (let i = 0; i < this.variablePairs.length; i++) {
        let aux = this.variablePairs[i].x;
        this.variablePairs[i].x = this.variablePairs[i].y;
        this.variablePairs[i].y = aux;
      }
      this.requestData();
    },
    updateUrl () {
      // Update URL
      const newQuery = {};
      const oldQuery = this.$route.query;
      for (let i in oldQuery) {
        if (oldQuery.hasOwnProperty(i)) {
          newQuery[i] = oldQuery[i];
        }
      }
      newQuery.optRangeX = this.range.x;
      newQuery.optRangeY = this.range.y;
      newQuery.optRangeZ = this.zRange;
      newQuery.optGrid = String(this.grid);
      newQuery.optMeter = this.meter;
      newQuery.zType = this.zType;
      if (this.origin) {
        newQuery.origin = this.origin;
      }
      if (this.filter) {
        newQuery.filter = this.filter;
      }
      this.$router.push({ query: newQuery });
    },
    listVariableIds () {
      let variableIds = [];
      if (this.zVariables) {
        const zNotNullVariables = this.zVariables.filter(zVariable => zVariable !== null);
        variableIds = [...new Set(zNotNullVariables.map(zVariable => String(zVariable)))];
      }

      for (let i = 0; i < this.variablePairs.length; i++) {
        if (!variableIds.includes(this.variablePairs[i].x)) {
          variableIds.push(this.variablePairs[i].x);
        }
        if (!variableIds.includes(this.variablePairs[i].y)) {
          variableIds.push(this.variablePairs[i].y);
        }
      }
      return variableIds;
    },
    onClickDownload () {
      if (this.variablePairs.length > 1) {
        this.$refs.variablePairSelectModal.show();
      } else {
        explorerToCSV.downloadCorrelation(this.getCorrelationData.variables, this.getLocale);
      }
    },
    onPairSelected (data) {
      let csvData = [];
      csvData.push(this.getVariableObjectById(parseInt(data.value.x)));
      csvData.push(this.getVariableObjectById(parseInt(data.value.y)));
      const selectedIndex = this.variablePairs.indexOf(data.value);
      if (this.zVariables !== undefined && this.zVariables[selectedIndex]) {
        csvData.push(this.getVariableObjectById(this.zVariables[selectedIndex]));
      }
      explorerToCSV.downloadCorrelation(csvData, this.getLocale);
    },
    getVariableObjectById (id) {
      return this.getCorrelationData.variables.find(variable => {
        return variable.metadata.id === id;
      });
    },
    getSampledVariables () {
      return this.getCorrelationData.variables.filter(variable => variable.sampled);
    }
  }
};

const getInitialStartDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapBegin;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};

const getInitialEndDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapEnd;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};

const getScatterSize = (numberOfScatters, type) => {
  const scattersContainerSize = getScattersContainerSize();

  let scatterWidth = 0;
  let scatterHeight = 0;
  switch (numberOfScatters) {
    case 1:
      scatterWidth = scattersContainerSize.width;
      scatterHeight = scattersContainerSize.height;
      break;

    case 2:
      scatterWidth = scattersContainerSize.width / 2.1;
      scatterHeight = scattersContainerSize.height;
      break;

    case 3:
    case 4:
      scatterWidth = scattersContainerSize.width / 2.1;
      scatterHeight = scattersContainerSize.height / 2.1;
      break;
  }

  return {
    width: scatterWidth,
    height: scatterHeight
  };
};

const getScattersContainerSize = () => {
  const scatterContainerParent = document.getElementById('scatters-container-parent');
  const scatterContainer = document.getElementById('scatters-container');
  const chartMeter = document.getElementById('chart-meter');

  let chartMeterWidth = 0;
  if (chartMeter) {
    chartMeterWidth = chartMeter.offsetWidth + 2;
  }

  return {
    width: scatterContainerParent.offsetWidth - chartMeterWidth,
    height: scatterContainer.offsetHeight
  };
};

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .container {
    padding-top: $sp-margin;
    padding-bottom: $sp-margin;
    flex-grow: 1;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
  .scatters-container-parent {
    display: flex;
    height: available;
    flex: 1;
  }

  .scatters-container {
    height: available;
    flex: 1;
    width: 100%;
  }

  .chart-meter-container {
    float: right;
  }

  .form-check {
    display: inline;
  }

  .header {
    display: flex;
    justify-content: space-between;

    &__title {
      margin-bottom: $sp-margin;
      @media (min-width: $breakpoint) {
        display: flex;
        h1 {
          margin-bottom: 0;
          margin-right: $sp-margin;
          padding-right: $sp-margin;
        }
      }
    }
  }

  .chevron {
    font-size: $fs-2xl;
    color: $jamaicol;
    margin-left: auto;
    margin-right: $sp-margin-smaller;
    cursor: pointer;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
  }

  .update-button {
    margin-left: $sp;
  }

  .flex {
    display: flex;
    align-items: center;
  }

  .icon {
    font-size: $fs;
    color: $jamaicol;
    margin-left: $sp-margin;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerCorrelation.vue